import { useEffect, useRef, useState } from 'react';
import type { StudioRecipe } from '@sr-test-wizard/shared';
import { useStudioRecipesStore } from '../../state/studioRecipesStore';
import { studioSlugClient } from './slug';
import { StudioRecipeForm } from './StudioRecipeForm';
import { ConfluencePanel } from './ConfluencePanel';
import {
  studioRecipesApi,
  type RecipeListEntry,
  type SourceStatusResponse,
} from '../../lib/studioRecipesApi';

const BLANK_RECIPE: StudioRecipe = {
  studio: '',
  version: 1,
  playUrlMode: 'base',
  urlTemplate: '',
  playUrlBase: undefined,
  marketCodeMap: {},
  defaults: {
    lang: 'en',
    currency: null,
    notes: null,
    credentials: { user: null, password: null },
  },
  perMarketOverrides: {},
  launchGuideMd: '',
  source: null,
  clientBriefMd: null,
  aiKnowledgeMd: null,
  // Defaults to "configure GitLab project later via the form" - the user
  // can leave it null and add it once they know the project id.
  gitlab: null,
};

function StudioBadges({
  entry,
  sourceStatus,
}: {
  entry: RecipeListEntry;
  sourceStatus: SourceStatusResponse | null;
}) {
  return (
    <>
      <span className="badge small mono">{entry.slug}</span>
      {entry.source === 'user' ? (
        <span className="badge small info" title="Edited via the app; overrides the bundled seed if any.">
          User
        </span>
      ) : (
        <span className="badge small grey" title="Bundled with the app; not yet edited.">
          Bundled
        </span>
      )}
      {entry.status === 'invalid' && (
        <span className="badge small bad" title={entry.error ?? undefined}>
          Invalid
        </span>
      )}
      {entry.status === 'missing' && <span className="badge small warn">Missing</span>}
      {sourceStatus?.kind === 'confluence' && (
        <span
          className={`badge small ${sourceStatus.stale ? 'bad' : 'info'}`}
          title={
            sourceStatus.stale
              ? `Confluence page is at v${sourceStatus.upstreamVersion}, recipe was synced from v${sourceStatus.localVersion}.`
              : 'Linked to Confluence and up to date.'
          }
        >
          {sourceStatus.stale ? '● Stale' : '● Synced'}
        </span>
      )}
      {entry.hasUser && entry.hasBundled && (
        <span className="badge small purple" title="A bundled seed exists; reverting drops the user override.">
          Override
        </span>
      )}
    </>
  );
}

/**
 * Searchable combobox for picking which studio to view/edit. Lists all
 * recipes in a small dropdown panel so the editor below isn't buried under
 * 20+ inline cards. Type to filter by display name or slug; click to load.
 */
function StudioPicker({
  list,
  sourceStatuses,
  activeSlug,
  onPick,
}: {
  list: RecipeListEntry[];
  sourceStatuses: Record<string, SourceStatusResponse>;
  activeSlug: string | null;
  onPick: (slug: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', onClick);
      document.addEventListener('keydown', onKey);
      return () => {
        document.removeEventListener('mousedown', onClick);
        document.removeEventListener('keydown', onKey);
      };
    }
    return undefined;
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? list.filter((e) => e.studio.toLowerCase().includes(q) || e.slug.toLowerCase().includes(q))
    : list;

  const activeEntry = list.find((e) => e.slug === activeSlug) ?? null;

  return (
    <div ref={wrapperRef} style={{ position: 'relative', flex: 1, minWidth: 0, maxWidth: 560 }}>
      <input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={
          activeEntry
            ? `${activeEntry.studio}  —  type to switch studio…`
            : `Search ${list.length} studios… (name or slug)`
        }
        style={{ width: '100%' }}
      />
      {open && (
        <div
          className="card"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 30,
            maxHeight: 360,
            overflowY: 'auto',
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
          }}
        >
          {filtered.length === 0 ? (
            <div className="muted small" style={{ padding: 10 }}>
              No studios match “{query}”.
            </div>
          ) : (
            filtered.map((entry) => {
              const active = entry.slug === activeSlug;
              return (
                <button
                  key={entry.slug}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onPick(entry.slug);
                    setOpen(false);
                    setQuery('');
                  }}
                  style={{
                    background: active ? 'var(--accent)' : 'transparent',
                    color: active ? '#fff' : 'inherit',
                    border: `1px solid ${active ? 'var(--accent)' : 'transparent'}`,
                    borderRadius: 6,
                    padding: '6px 8px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    flexWrap: 'wrap',
                  }}
                >
                  <strong style={{ minWidth: 0 }}>{entry.studio}</strong>
                  <StudioBadges entry={entry} sourceStatus={sourceStatuses[entry.slug] ?? null} />
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

interface AddStudioFormProps {
  onClose: () => void;
  onCreated: (slug: string, recipe: StudioRecipe) => void;
}

/**
 * Inline panel for creating a new recipe. Two paths:
 *   1. Drop a wiki PDF + a studio name -> server drafts a recipe, the form
 *      opens with that draft (not yet saved).
 *   2. Click "Start blank" -> the form opens with an empty template named
 *      after whatever was in the studio input.
 */
function AddStudioPanel({ onClose, onCreated }: AddStudioFormProps) {
  const drafting = useStudioRecipesStore((s) => s.drafting);
  const draftError = useStudioRecipesStore((s) => s.draftError);
  const draftFromPdf = useStudioRecipesStore((s) => s.draftFromPdf);

  const [studio, setStudio] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const slug = studio ? studioSlugClient(studio) : '';

  const blank = () => {
    if (!studio.trim()) return;
    onCreated(studioSlugClient(studio), { ...BLANK_RECIPE, studio: studio.trim() });
  };
  const draft = async () => {
    if (!file || !studio.trim()) return;
    try {
      const result = await draftFromPdf(file, studio.trim());
      setWarnings(result.warnings);
      onCreated(studioSlugClient(studio), result.recipe);
    } catch {
      // surfaced via store error
    }
  };

  return (
    <div className="card">
      <div className="row" style={{ gap: 8 }}>
        <h3 style={{ margin: 0 }}>Add studio</h3>
        <span className="spacer" />
        <button type="button" className="btn small btn-ghost" onClick={onClose}>
          Cancel
        </button>
      </div>
      <div className="flex-col" style={{ gap: 8, marginTop: 10 }}>
        <div className="row" style={{ gap: 8 }}>
          <label className="muted small" style={{ minWidth: 100 }}>
            Studio name
          </label>
          <input
            value={studio}
            onChange={(e) => setStudio(e.target.value)}
            placeholder="Octoplay"
            style={{ flex: 1 }}
          />
          {slug && <span className="badge small mono">{slug}</span>}
        </div>
        <div className="row" style={{ gap: 8 }}>
          <label className="muted small" style={{ minWidth: 100 }}>
            Wiki PDF
          </label>
          <input
            ref={fileInput}
            type="file"
            accept="application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          {file && <span className="muted small">{file.name} ({Math.round(file.size / 1024)} KB)</span>}
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button
            type="button"
            className="btn small btn-primary"
            disabled={!file || !studio.trim() || drafting}
            onClick={() => void draft()}
          >
            {drafting ? 'Drafting (~30s)...' : 'Draft from PDF (AI)'}
          </button>
          <button
            type="button"
            className="btn small"
            disabled={!studio.trim()}
            onClick={blank}
          >
            Start blank
          </button>
        </div>
        {draftError && <p style={{ color: 'var(--bad)', margin: 0 }}>{draftError}</p>}
        {warnings.length > 0 && (
          <ul className="muted small" style={{ margin: 0, paddingLeft: 18 }}>
            {warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        )}
        <p className="muted small" style={{ margin: 0 }}>
          The PDF is sent to the configured OpenAI model (gpt-5.5) which drafts
          a recipe matching the schema. You will review and edit the result
          before anything is saved.
        </p>
      </div>
    </div>
  );
}

export function StudiosPage() {
  const list = useStudioRecipesStore((s) => s.list);
  const listLoading = useStudioRecipesStore((s) => s.listLoading);
  const listError = useStudioRecipesStore((s) => s.listError);
  const current = useStudioRecipesStore((s) => s.current);
  const currentLoading = useStudioRecipesStore((s) => s.currentLoading);
  const currentError = useStudioRecipesStore((s) => s.currentError);
  const saving = useStudioRecipesStore((s) => s.saving);
  const saveError = useStudioRecipesStore((s) => s.saveError);
  const loadList = useStudioRecipesStore((s) => s.loadList);
  const loadRecipe = useStudioRecipesStore((s) => s.loadRecipe);
  const setDraft = useStudioRecipesStore((s) => s.setDraft);
  const clearCurrent = useStudioRecipesStore((s) => s.clearCurrent);
  const save = useStudioRecipesStore((s) => s.save);
  const remove = useStudioRecipesStore((s) => s.remove);

  const [adding, setAdding] = useState(false);
  const [sourceStatuses, setSourceStatuses] = useState<Record<string, SourceStatusResponse>>({});

  useEffect(() => {
    void loadList();
  }, [loadList]);

  // After the list loads, fan out a status probe for each entry so the rows
  // can render a stale badge. We do this lazily (no Promise.all) to avoid
  // hammering Confluence with bursts when the user has many studios.
  useEffect(() => {
    let cancelled = false;
    async function pollAll() {
      for (const entry of list) {
        if (cancelled) return;
        try {
          const data = await studioRecipesApi.sourceStatus(entry.slug);
          if (cancelled) return;
          setSourceStatuses((m) => ({ ...m, [entry.slug]: data }));
        } catch {
          // soft-fail; the list still renders without a badge
        }
      }
    }
    if (list.length > 0) void pollAll();
    return () => {
      cancelled = true;
    };
  }, [list]);

  const activeEntry = list.find((e) => e.slug === current?.slug);

  return (
    <div className="flex-col">
      <div className="row">
        <h1 style={{ margin: 0 }}>Studios</h1>
        <span className="spacer" />
        {!adding && (
          <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>
            Add studio
          </button>
        )}
      </div>

      <p className="muted small" style={{ margin: 0 }}>
        Studio recipes drive Phase 2 (Generate Environment Links) and Phase 3
        (Pull Source Code). Pick a studio from the list to view or edit its
        recipe, or add a new one by uploading its wiki PDF.
      </p>

      {adding && (
        <AddStudioPanel
          onClose={() => setAdding(false)}
          onCreated={(slug, recipe) => {
            setDraft(slug, recipe, 'user');
            setAdding(false);
          }}
        />
      )}

      {listError && <p style={{ color: 'var(--bad)' }}>{listError}</p>}

      <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {listLoading && list.length === 0 ? (
          <p className="muted small" style={{ margin: 0 }}>Loading studios…</p>
        ) : (
          <>
            <StudioPicker
              list={list}
              sourceStatuses={sourceStatuses}
              activeSlug={current?.slug ?? null}
              onPick={(slug) => void loadRecipe(slug)}
            />
            <span className="muted small">{list.length} total</span>
          </>
        )}
        {!listLoading && list.length === 0 && !adding && (
          <p className="muted" style={{ margin: 0 }}>
            No studios yet. Click “Add studio” to create the first one.
          </p>
        )}
      </div>

      {activeEntry && current && (
        <div
          className="card"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            padding: '8px 10px',
            borderColor: 'var(--accent)',
          }}
        >
          <strong>{activeEntry.studio}</strong>
          <StudioBadges entry={activeEntry} sourceStatus={sourceStatuses[activeEntry.slug] ?? null} />
          <span className="spacer" />
          <button type="button" className="btn small btn-ghost" onClick={clearCurrent}>
            Close editor
          </button>
        </div>
      )}

      {currentLoading && <p className="muted small">Loading recipe...</p>}
      {currentError && <p style={{ color: 'var(--bad)' }}>{currentError}</p>}

      {current && current.recipe && (
        <>
          <ConfluencePanel
            slug={current.slug}
            recipe={current.recipe}
            onSynced={(result) => {
              // The server already persisted the synced recipe; mirror it
              // into the in-memory store so the form fields, brief preview,
              // and stale badge all reflect the new state without an extra
              // round-trip.
              setDraft(result.slug, result.recipe, 'user');
              void loadList();
              setSourceStatuses((m) => {
                const next = { ...m };
                delete next[result.slug];
                return next;
              });
              // Refresh the row-level status badge so it flips from Stale
              // back to Synced after the user resyncs.
              void studioRecipesApi
                .sourceStatus(result.slug)
                .then((s) => setSourceStatuses((m) => ({ ...m, [result.slug]: s })))
                .catch(() => {});
            }}
          />
          <StudioRecipeForm
            slug={current.slug}
            source={current.source}
            recipe={current.recipe}
            hasUserOverride={
              (activeEntry?.hasUser ?? false) ||
              // We just drafted/created in-memory; treat as user until saved.
              current.source === 'user'
            }
            saving={saving}
            saveError={saveError}
            onSave={async (recipe) => {
              // The form strips source + clientBriefMd from its draft state
              // (they're not user-editable in the form). Preserve whatever
              // came from the server / sync so a manual Save doesn't wipe
              // the Confluence link.
              await save(current.slug, {
                ...recipe,
                source: current.recipe?.source ?? null,
                clientBriefMd: current.recipe?.clientBriefMd ?? null,
              });
            }}
            onDelete={
              activeEntry?.hasUser
                ? async () => {
                    if (!confirm('Remove this user override? The bundled seed (if any) will load on the next refresh.')) {
                      return;
                    }
                    await remove(current.slug);
                  }
                : undefined
            }
            onClose={clearCurrent}
          />
        </>
      )}
      {current && !current.recipe && current.error && (
        <div className="card" style={{ borderColor: 'var(--bad)' }}>
          <h3>Recipe is invalid</h3>
          <p style={{ color: 'var(--bad)' }}>{current.error}</p>
          <p className="muted small">
            Fix the file on disk or revert the user override.
          </p>
        </div>
      )}
    </div>
  );
}
